import React from 'react'
import { Field } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'
import { Box, Button, CircularProgress, DialogActions, DialogContent, Typography } from '@mui/material'
import { UpdateQueryOptions } from '@apollo/client'
import { FormDatePicker, GraphQLForm } from 'src/form'
import { ClosableDialogTitle } from 'src/legacy/components/mui'
import { trackProgrmaStartDateUpdate } from 'src/mixpanel/api'
import { useGetEarliestProgramStartDateQuery, useUpdateProgramStartDateMutation } from '../patientQueries.generated'
import { getProgramStartDateSuggestion, isStartDateDisabled, programStartDateSchema } from './dateHelpers'

type Props = {
  programStartDate: string
  uid: string
  close: () => void
  cancel: () => void
}

const SetProgramStartDateSchema = (
  earliestProgramStartDate: string | null | undefined,
  isAbTestWeekZeroRemovalVariant: boolean
) =>
  Yup.object({
    uid: Yup.string().required(),
    programStartDate: programStartDateSchema(earliestProgramStartDate, isAbTestWeekZeroRemovalVariant),
  }).required()

type ProgramStartDateForm = Yup.InferType<ReturnType<typeof SetProgramStartDateSchema>>

const SetProgramStartDateForm = ({ programStartDate, uid, close, cancel }: Props) => {
  const [updateProgramStartDate] = useUpdateProgramStartDateMutation()
  const { data: earliestProgramStartDateData, loading: earliestProgramStartDateLoading } =
    useGetEarliestProgramStartDateQuery({ variables: { uid } })

  const handleSubmit = async ({ variables }: UpdateQueryOptions<ProgramStartDateForm>) => {
    await updateProgramStartDate({ variables })
    trackProgrmaStartDateUpdate(moment(variables?.programStartDate).diff(programStartDate, 'weeks'))
  }

  if (earliestProgramStartDateLoading) {
    return (
      <>
        <ClosableDialogTitle onClose={close}>Change program start date</ClosableDialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={cancel}>
            Cancel
          </Button>
        </DialogActions>
      </>
    )
  }

  const isAbTestWeekZeroRemovalVariant = earliestProgramStartDateData?.patient.isAbTestWeekZeroRemovalVariant ?? false
  const earliestStartDate = earliestProgramStartDateData?.patient.earliestProgramStartDate
  const suggestedStartDate = getProgramStartDateSuggestion(earliestStartDate, isAbTestWeekZeroRemovalVariant)

  return (
    <GraphQLForm
      initialValues={{ uid, programStartDate: suggestedStartDate.format('YYYY-MM-DD') }}
      onSubmit={handleSubmit}
      onSuccess={close}
      validationSchema={SetProgramStartDateSchema(earliestStartDate, isAbTestWeekZeroRemovalVariant)}
      render={({ isSubmitting }) => (
        <>
          <ClosableDialogTitle onClose={close}>Change program start date</ClosableDialogTitle>
          <DialogContent>
            <Typography>Current start date: {moment(programStartDate).format('M/D/YYYY')}</Typography>
            <Field
              component={FormDatePicker}
              label="Start date"
              fullWidth
              name="programStartDate"
              shouldDisableDate={(date: moment.Moment) =>
                isStartDateDisabled(date, earliestStartDate, isAbTestWeekZeroRemovalVariant)
              }
            />
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={cancel}>
              Cancel
            </Button>
            <Button color="primary" disabled={isSubmitting} type="submit">
              Confirm
            </Button>
          </DialogActions>
        </>
      )}
    />
  )
}

export default SetProgramStartDateForm
